import React from 'react';
import {
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  StyleSheet,
  View,
  type DimensionValue,
  type StyleProp,
  type ViewStyle,
} from 'react-native';
import {useSafeAreaInsets} from 'react-native-safe-area-context';

import {useBreakpoint} from '@/shared/lib/breakpoints';
import {layout, spacing, useTheme} from '@/shared/ui/theme';

/**
 * The page frame: background from the palette, safe-area padding, and — for forms —
 * a scroll view that gets out from under the keyboard.
 *
 * `scroll` is opt-in rather than always-on: a tab screen that fits should not bounce.
 *
 * The frame also caps content and centres it — but only while the window is NARROW. A
 * phone reads at 560pt, and pinning a desktop to 560 is what made the browser build
 * look like a screenshot with grey either side. Past the desktop breakpoint the cap is
 * lifted entirely and the gutter doubles: the page is as wide as the window, which is
 * the room the grids, tables and split views on the screens above spend.
 *
 * There used to be a second ceiling at 1180pt for exactly the same reason the phone has
 * one — an uncapped feed row makes the eye travel from a name on the far left to its
 * timestamp on the far right. That is a real problem and it is a problem about a ROW,
 * not about a page: on a 1920 monitor it left a quarter of the screen as empty margin
 * and made the whole product look shrunk. Rows that need to stay readable cap
 * themselves (see `SplitView`, and the `flex`/`width` weights a `DataTable` column
 * carries); the page does not do it for them.
 *
 * `width="form"` is the opt-in for the screens that ARE a document — one person, one
 * device, one settings page — and it is the only thing that still holds a column open
 * on a wide window.
 *
 * ## The floor is paper
 *
 * It used to carry a faint 32pt grid on the web build, so that the background would
 * "stop being paper" and read as an instrument. That is the one decoration this
 * redesign misses and the one it is most sure about dropping: shadcn's page is
 * `bg-background` and nothing else, and a grid showing through 300pt of empty workspace
 * either side of a centred column is texture with no job.
 */
const styles = StyleSheet.create({
  root: {flex: 1},
  flex: {flex: 1},
  column: {
    flex: 1,
    width: '100%',
    maxWidth: layout.maxContentWidth,
    alignSelf: 'center',
  },
  scrollBody: {
    flexGrow: 1,
    width: '100%',
    maxWidth: layout.maxContentWidth,
    alignSelf: 'center',
    paddingBottom: spacing(6),
  },
  // No ceiling: the page is the window. `'100%'` rather than dropping the key, because
  // it has to OUTRANK `column`/`scrollBody`'s phone cap in the same style array.
  wide: {maxWidth: '100%'},
  // A sign-in form does not get wider because the window did: a 1900pt-wide field is
  // harder to fill in than a 400pt one, and a login screen that spans a monitor reads
  // as a website, not an app.
  narrow: {maxWidth: layout.authContentWidth},
  // One person, one device, one settings page: a detail screen is a document, and a
  // document has a column.
  form: {maxWidth: layout.formContentWidth},
});

/**
 * The same width rule as `Screen`, for the lists that draw their own frame.
 *
 * A FlatList cannot live inside Screen's padded column — it has to own the scroll, or
 * pull-to-refresh and the sticky header stop working — so those screens set
 * `gutter={false}` and pay the inset on the list's own content container. This hook is
 * how they stay in step with Screen instead of hard-coding 560 and stranding the
 * desktop build in a phone column, which is exactly what happened.
 */
export type ContentInsets = {
  maxWidth: DimensionValue;
  paddingHorizontal: number;
};

export function useContentInsets(): ContentInsets {
  const {isDesktop} = useBreakpoint();
  return {
    maxWidth: isDesktop ? '100%' : layout.maxContentWidth,
    paddingHorizontal: isDesktop ? layout.desktopGutter : layout.gutter,
  };
}

export type ScreenProps = {
  children: React.ReactNode;
  /** Wrap the content in a scroll view (forms, long content). */
  scroll?: boolean;
  /** Skip the top inset when a navigator header already paid it. */
  edgeTop?: boolean;
  /** Drop the horizontal gutter — for content that draws its own edges. */
  gutter?: boolean;
  /**
   * `auto` follows the window (phone column → desktop column, for boards and lists).
   * `form` is a reading column for a single record or settings page. `narrow` is the
   * tightest — auth and onboarding, where the whole screen is one short task.
   */
  width?: 'auto' | 'form' | 'narrow';
  style?: StyleProp<ViewStyle>;
  testID?: string;
};

export function Screen({
  children,
  scroll = false,
  edgeTop = true,
  gutter = true,
  width = 'auto',
  style,
  testID,
}: ScreenProps) {
  const p = useTheme();
  const insets = useSafeAreaInsets();
  const {isDesktop} = useBreakpoint();
  const pad = {
    paddingTop: edgeTop ? insets.top + spacing(2) : spacing(isDesktop ? 4 : 2),
    paddingBottom: insets.bottom,
  };
  const inset = gutter
    ? {paddingHorizontal: isDesktop ? layout.desktopGutter : layout.gutter}
    : null;
  const narrow = width === 'narrow';
  const form = width === 'form';

  return (
    <View
      testID={testID}
      style={[styles.root, {backgroundColor: p.background}, pad]}>
      <KeyboardAvoidingView
        style={styles.flex}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
        {scroll ? (
          <ScrollView
            contentContainerStyle={[
              styles.scrollBody,
              isDesktop && styles.wide,
              form && styles.form,
              narrow && styles.narrow,
              inset,
              style,
            ]}
            showsVerticalScrollIndicator
            keyboardShouldPersistTaps="handled">
            {children}
          </ScrollView>
        ) : (
          <View
            style={[
              styles.column,
              isDesktop && styles.wide,
              form && styles.form,
              narrow && styles.narrow,
              inset,
              style,
            ]}>
            {children}
          </View>
        )}
      </KeyboardAvoidingView>
    </View>
  );
}
